import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import { Card, Typography } from "@mui/material";

// Vision UI Dashboard React components
import VuiBox from "components/VuiBox";

// Vision UI Dashboard React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

// Dashboard layout components
import WelcomeMark from "layouts/dashboard/components/WelcomeMark";
import Projects from "layouts/dashboard/components/Projects";
import OrderOverview from "layouts/dashboard/components/OrderOverview";
import SatisfactionRate from "layouts/dashboard/components/SatisfactionRate";
import ReferralTracking from "layouts/dashboard/components/ReferralTracking";
import FraudstersOverview from "layouts/dashboard/components/FraudstersOverview";
import ActiveUsers from "layouts/dashboard/components/ActiveUsers";

// React icons
import { IoBarChart, IoShieldCheckmarkOutline, IoWarningOutline } from "react-icons/io5";
import { FaUserSecret } from "react-icons/fa";

// Services
import { getDashboardStats } from "services/dashboard";
import { useAuth } from "context/AuthContext";


// Comentario temporario para acionar pipelines de frontend durante diagnosticos.
function Dashboard() {
  const { profile, loading: authLoading, companyScopeQuery } = useAuth();
  const [stats, setStats] = useState({
    leadsMonth: { value: 0, percentage: null },
    lowRisk: { value: 0, percentage: null },
    suspects: { value: 0, percentage: null },
    fraudsters: { value: 0, percentage: null },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [totalForms, setTotalForms] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalFraudsters, setTotalFraudsters] = useState(0);
  const [last7DaysStats, setLast7DaysStats] = useState(null);
  const [fraudstersOverview, setFraudstersOverview] = useState([]);
  const [monthlyLeadsChart, setMonthlyLeadsChart] = useState({ data: [], options: {} });
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

  useEffect(() => {
    if (authLoading) return;

    if (!profile) {
      setShowWelcomeMessage(true);
      setIsLoading(false);
      return;
    }

    const hasAccess = profile.is_superuser || profile.company_id;
    if (!hasAccess) {
      setShowWelcomeMessage(true);
      setIsLoading(false);
      return;
    }

    const fetchStats = async () => {
      setIsLoading(true);
      setShowWelcomeMessage(false);
      try {
        const data = await getDashboardStats(companyScopeQuery);
        setStats(data.last30Days);
        setTotalForms(data.allTime.totalFormsSubmitted);
        setTotalLeads(data.allTime.totalLeads);
        setTotalFraudsters(data.allTime.totalFraudsters);
        setLast7DaysStats(data.last7Days);
        setFraudstersOverview(data.fraudstersOverview);
        setMonthlyLeadsChart({
          data: data.monthlyLeadsChart.map((item) => item.leads),
          options: { xaxis: { categories: data.monthlyLeadsChart.map((item) => item.month) } },
        });
      } catch (error) {
        console.error("Erro ao buscar estatísticas do dashboard:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [authLoading, profile, companyScopeQuery]);

  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) {
      return { color: "secondary", text: "" };
    }
    const sign = percentage > 0 ? "+" : "";
    return {
      color: percentage > 0 ? "success" : "error",
      text: `${sign}${percentage}%`,
    };
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3}>
          <Typography>Carregando...</Typography>
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (showWelcomeMessage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3} display="flex" justifyContent="center" alignItems="center" height="80vh">
            <WelcomeMark />
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Leads Mês" }}
                count={stats.leadsMonth.value}
                percentage={formatPercentage(stats.leadsMonth.percentage)}
                icon={{ color: "info", component: <IoBarChart size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Baixo Risco" }}
                count={stats.lowRisk.value}
                percentage={formatPercentage(stats.lowRisk.percentage)}
                icon={{ color: "info", component: <IoShieldCheckmarkOutline size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Suspeitos" }}
                count={stats.suspects.value}
                percentage={formatPercentage(stats.suspects.percentage)}
                icon={{ color: "info", component: <IoWarningOutline size="22px" color="white" /> }}
              />
            </Grid>
            <Grid item xs={12} md={6} xl={3}>
              <MiniStatisticsCard
                title={{ text: "Fraudadores" }}
                count={stats.fraudsters.value}
                percentage={formatPercentage(stats.fraudsters.percentage)}
                icon={{ color: "info", component: <FaUserSecret size="22px" color="white" /> }}
              />
            </Grid>
          </Grid>
        </VuiBox>
        {/* O restante dos seus componentes do dashboard continua aqui */}
        <VuiBox mb={3}>
          <Grid container spacing="18px">
            <Grid item xs={12} lg={12} xl={5}>
              <WelcomeMark />
            </Grid>
            <Grid item xs={12} lg={6} xl={3}>
              <SatisfactionRate totalFraudsters={totalFraudsters} />
            </Grid>
            <Grid item xs={12} lg={6} xl={4}>
              <ReferralTracking
                totalLeads={totalLeads}
                totalFraudsters={totalFraudsters}
                totalFormsSubmitted={totalForms}
              />
            </Grid>
          </Grid>
        </VuiBox>
        <VuiBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={6} xl={7}>
              <FraudstersOverview fraudsters={fraudstersOverview} />
            </Grid>
            <Grid item xs={12} lg={6} xl={5}>
              <ActiveUsers stats={last7DaysStats} chartData={monthlyLeadsChart.data} chartOptions={monthlyLeadsChart.options} />
            </Grid>
          </Grid>
        </VuiBox>
        <Grid container spacing={3} direction="row" justifyContent="center" alignItems="stretch">
          <Grid item xs={12} md={6} lg={8}>
            <Projects />
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <OrderOverview isLoading={isLoading} />
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
